import { waitForInitialAuthState } from "@/services/superAdminAccess";

export const DASHBOARD_SECTIONS = Object.freeze([
  "overview",
  "members",
  "roles",
  "monetary",
]);

const routeHasMeta = (to, key) =>
  to?.meta?.[key] === true ||
  (Array.isArray(to?.matched) &&
    to.matched.some((record) => record.meta?.[key] === true));

export const routeRequiresAuth = (to) => routeHasMeta(to, "requiresAuth");
export const routeIsGuestOnly = (to) => routeHasMeta(to, "guestOnly");

export const safeReturnTo = (candidate, fallback = "/") => {
  if (typeof candidate !== "string") return fallback;

  const value = candidate.trim();
  if (!value.startsWith("/") || value.startsWith("//")) return fallback;

  try {
    const parsed = new URL(value, "https://unit.local");
    if (parsed.origin !== "https://unit.local") return fallback;
    if (["/login", "/register"].includes(parsed.pathname)) return fallback;
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch (_error) {
    return fallback;
  }
};

export const authenticationRouteGuard = async (
  to,
  resolveUser = waitForInitialAuthState,
) => {
  if (!routeRequiresAuth(to) && !routeIsGuestOnly(to)) return true;

  const user = await resolveUser();

  if (routeRequiresAuth(to) && !user) {
    return {
      name: "Login",
      query: { returnTo: safeReturnTo(to.fullPath) },
      replace: true,
    };
  }

  if (routeIsGuestOnly(to) && user) {
    return safeReturnTo(to.query?.returnTo);
  }

  return true;
};

export const sectionRouteGuard = (
  to,
  routeName,
  allowedSections,
  defaultSection = "overview",
) => {
  const legacySection = to.query?.section;
  const requestedSection = to.params?.section || legacySection || defaultSection;
  const section = allowedSections.includes(requestedSection)
    ? requestedSection
    : defaultSection;
  const hasLegacyQuery = typeof legacySection === "string";

  if (to.params?.section === section && !hasLegacyQuery) return true;

  const { section: _legacySection, ...query } = to.query || {};
  return {
    name: routeName,
    params: { section },
    query,
    hash: to.hash,
    replace: true,
  };
};

export const dashboardSectionGuard = (to) =>
  sectionRouteGuard(to, "dashboard", DASHBOARD_SECTIONS);
